import React from 'react';
import { useTranslation } from 'react-i18next';
import { s } from '../helpers';

export interface ListRowItem {
  accountId: string;
  cmName: string;
  alias: string;
  platform: string;
  stage: string;
  stageColor: string;
  stageBg: string;
  capital: string;
  balance: string;
  pnl: string;
  pnlColor: string;
  dd: string;
  ddColor: string;
  status: string;
  statusColor: string;
  onClick: () => void;
}

export interface ListDrilldownModalProps {
  isOpen: boolean;
  closeModal: () => void;
  title: string;
  sub: string;
  count: number;
  search: string;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  rows: ListRowItem[];
  loading?: boolean;
  sortBy?: string;
  order?: 'desc' | 'asc';
  onSortByChange?: (sortBy: string) => void;
  onOrderToggle?: () => void;
  page?: number;
  lastPage?: number;
  onPageChange?: (page: number) => void;
}

export const ListDrilldownModal: React.FC<ListDrilldownModalProps> = ({
  isOpen,
  closeModal,
  title,
  sub,
  count,
  search,
  onSearch,
  rows,
  loading = false,
  sortBy = 'initial_balance',
  order = 'desc',
  onSortByChange,
  onOrderToggle,
  page = 1,
  lastPage = 1,
  onPageChange
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const listEmpty = rows.length === 0;

  return (
    <div
      onClick={closeModal}
      style={s(
        `position:fixed;inset:0;background:rgba(1,37,60,0.45);z-index:9999;display:flex;align-items:flex-start;justify-content:center;padding:5vh 16px;overflow-y:auto`
      )}
    >
      <div
        onClick={stop}
        style={s(
          `width:100%;max-width:1040px;background:#fff;border-radius:14px;box-shadow:0 24px 64px rgba(1,37,60,0.3);overflow:hidden;display:flex;flex-direction:column;max-height:90vh`
        )}
      >
        {/* Header */}
        <div
          style={s(
            `display:flex;align-items:flex-start;justify-content:space-between;gap:12px;padding:18px 24px;border-bottom:1px solid #EEF0F3;flex:none`
          )}
        >
          <div style={s(`display:flex;flex-direction:column;gap:4px`)}>
            <div style={s(`display:flex;align-items:center;gap:8px`)}>
              <span style={s(`font-size:18px;font-weight:700;color:#0F1B2D`)}>
                {title}
              </span>
              <span
                style={s(
                  `padding:2px 8px;border-radius:12px;background:#F2F4F7;color:#344054;font-size:12px;font-weight:600`
                )}
              >
                {loading
                  ? '...'
                  : t('managerNetwork.drilldown.accountsCount', '{{count}} accounts', { count })}
              </span>
              {loading && (
                <span style={s(`font-size:12px;color:#B8893A;font-weight:500;display:flex;align-items:center;gap:4px`)}>
                  <i className="icon-refresh" style={s(`animation:spin 1s linear infinite`)}></i> {t('managerNetwork.drilldown.loading', 'Đang tải...')}
                </span>
              )}
            </div>
            <span style={s(`font-size:13px;color:#6B7280;text-wrap:pretty`)}>{sub}</span>
          </div>
          <button
            onClick={closeModal}
            style={s(
              `width:34px;height:34px;border-radius:8px;border:1px solid #E3E6EA;background:#fff;cursor:pointer;font-size:16px;color:#1B2430;display:flex;align-items:center;justify-content:center;flex:none`
            )}
          >
            <i className="icon-x"></i>
          </button>
        </div>

        {/* Toolbar: Search, Sort, Order */}
        <div
          style={s(
            `padding:12px 24px;border-bottom:1px solid #EEF0F3;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;background:#FAFBFC;flex:none`
          )}
        >
          <div
            style={s(
              `flex:1;min-width:240px;max-width:380px;height:38px;display:flex;align-items:center;gap:8px;padding:0 12px;border:1px solid #E3E6EA;border-radius:8px;background:#fff`
            )}
          >
            <i className="icon-search" style={s(`color:#6B7280`)}></i>
            <input
              value={search}
              onChange={onSearch}
              placeholder={t('managerNetwork.drilldown.searchPlaceholder', 'Tìm theo Tên, Account Number, hoặc Broker...')}
              style={s(`border:0;outline:0;flex:1;font-size:13.5px;min-width:0;background:transparent`)}
            />
          </div>

          {onSortByChange && (
            <div style={s(`display:flex;align-items:center;gap:8px;flex-wrap:wrap`)}>
              <span style={s(`font-size:12px;color:#6B7280;font-weight:500`)}>{t('managerNetwork.drilldown.sortBy', 'Sắp xếp:')}</span>
              <div style={s(`position:relative;display:flex;align-items:center`)}>
                <select
                  value={sortBy}
                  onChange={(e) => onSortByChange(e.target.value)}
                  style={s(
                    `height:36px;padding:0 28px 0 10px;border:1px solid #E3E6EA;border-radius:6px;background:#fff;font-size:12.5px;color:#1B2430;cursor:pointer;outline:none`
                  )}
                >
                  <option value="initial_balance">{t('managerNetwork.drilldown.initialCapital', 'Initial Capital')}</option>
                  <option value="balance">{t('managerNetwork.drilldown.currentBalance', 'Current Balance')}</option>
                </select>
                <i className="icon-chevron-down" style={s(`position:absolute;right:8px;pointer-events:none;font-size:11px;color:#6B7280`)}></i>
              </div>

              {onOrderToggle && (
                <button
                  onClick={onOrderToggle}
                  title={`${t('managerNetwork.drilldown.sortBy', 'Sắp xếp:')} ${order === 'desc' ? t('managerNetwork.drilldown.descending', 'Giảm dần') : t('managerNetwork.drilldown.ascending', 'Tăng dần')}`}
                  style={s(
                    `height:36px;padding:0 10px;display:flex;align-items:center;gap:5px;border:1px solid #E3E6EA;border-radius:6px;background:#fff;color:#1B2430;font-size:12px;cursor:pointer;font-weight:500`
                  )}
                >
                  <i className={order === 'desc' ? 'icon-arrow-down' : 'icon-arrow-up'} style={s(`font-size:12px;color:#B8893A`)}></i>
                  {order === 'desc' ? t('managerNetwork.drilldown.descending', 'Giảm dần') : t('managerNetwork.drilldown.ascending', 'Tăng dần')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Table List */}
        <div style={s(`overflow-x:auto;flex:1;overflow-y:auto;min-height:240px`)}>
          <div style={s(`min-width:880px`)}>
            <div
              style={s(
                `display:grid;grid-template-columns:minmax(200px,2fr) 76px 110px 100px 116px 100px 80px 116px;gap:8px;padding:10px 24px;background:#FAFAFB;font-size:11.5px;font-weight:600;color:#6B7280;text-transform:uppercase;letter-spacing:0.04em;position:sticky;top:0;z-index:2;border-bottom:1px solid #EEF0F3`
              )}
            >
              <span>{t('managerNetwork.drilldown.cols.account', 'Account')}</span>
              <span>{t('managerNetwork.drilldown.cols.platform', 'Platform')}</span>
              <span>{t('managerNetwork.drilldown.cols.stage', 'Stage')}</span>
              <span>{t('managerNetwork.drilldown.cols.capital', 'Capital')}</span>
              <span>{t('managerNetwork.drilldown.cols.balance', 'Balance')}</span>
              <span>{t('managerNetwork.drilldown.cols.pnl', 'P&L')}</span>
              <span>{t('managerNetwork.drilldown.cols.dd', 'DD')}</span>
              <span>{t('managerNetwork.drilldown.cols.status', 'Status')}</span>
            </div>

            {loading ? (
              <div style={s(`padding:48px;text-align:center;color:#6B7280;font-size:14px;display:flex;flex-direction:column;align-items:center;gap:8px`)}>
                <i className="icon-refresh" style={s(`font-size:24px;color:#B8893A;animation:spin 1s linear infinite`)}></i>
                <span>{t('managerNetwork.drilldown.loadingAccounts', 'Đang tải dữ liệu tài khoản...')}</span>
              </div>
            ) : rows && rows.length > 0 ? (
              rows.map((r, r_idx) => (
                <React.Fragment key={r_idx}>
                  <div
                    onClick={r.onClick}
                    style={s(
                      `display:grid;grid-template-columns:minmax(200px,2fr) 76px 110px 100px 116px 100px 80px 116px;gap:8px;align-items:center;padding:10px 24px;border-bottom:1px solid #F1F2F5;font-size:13px;cursor:pointer;transition:background 0.15s`
                    )}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#F9FAFB')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={s(`display:flex;flex-direction:column;min-width:0`)}>
                      <span
                        style={s(
                          `font-weight:600;color:#0F1B2D;white-space:nowrap;overflow:hidden;text-overflow:ellipsis`
                        )}
                      >
                        {r.cmName}
                      </span>
                      <span style={s(`font-size:11.5px;color:#6B7280`)}>
                        {r.accountId} · {r.alias}
                      </span>
                    </div>
                    <span style={s(`color:#1B2430`)}>{r.platform}</span>
                    <span>
                      <span
                        style={s(
                          `font-size:11.5px;font-weight:600;color:${r.stageColor};background:${r.stageBg};padding:3px 8px;border-radius:10px;white-space:nowrap`
                        )}
                      >
                        {r.stage}
                      </span>
                    </span>
                    <span>{r.capital}</span>
                    <span style={s(`font-weight:500`)}>{r.balance}</span>
                    <span style={s(`color:${r.pnlColor};font-weight:500`)}>{r.pnl}</span>
                    <span style={s(`color:${r.ddColor}`)}>{r.dd}</span>
                    <span style={s(`display:flex;align-items:center;gap:6px`)}>
                      <span style={s(`width:8px;height:8px;border-radius:50%;background:${r.statusColor}`)}></span>
                      {r.status}
                    </span>
                  </div>
                </React.Fragment>
              ))
            ) : (
              <div style={s(`padding:48px 32px;text-align:center;color:#6B7280;font-size:14px`)}>
                {t('managerNetwork.drilldown.noAccounts', 'Không tìm thấy tài khoản nào phù hợp với điều kiện tìm kiếm.')}
              </div>
            )}
          </div>
        </div>

        {/* Pagination footer */}
        {!loading && onPageChange && lastPage > 1 && (
          <div
            style={s(
              `padding:12px 24px;border-top:1px solid #EEF0F3;display:flex;align-items:center;justify-content:space-between;background:#FAFBFC;flex:none;font-size:13px;color:#475467`
            )}
          >
            <span>
              {t('managerNetwork.drilldown.pageOf', 'Trang {{page}} / {{lastPage}}', { page, lastPage })}
            </span>
            <div style={s(`display:flex;gap:8px`)}>
              <button
                disabled={page <= 1}
                onClick={() => onPageChange(page - 1)}
                style={s(
                  `padding:6px 12px;border:1px solid #D0D5DD;border-radius:6px;background:${page <= 1 ? '#F2F4F7' : '#fff'};color:${page <= 1 ? '#98A2B3' : '#344054'};cursor:${page <= 1 ? 'not-allowed' : 'pointer'};font-size:12.5px;font-weight:500`
                )}
              >
                {t('managerNetwork.drilldown.prev', 'Trước')}
              </button>
              <button
                disabled={page >= lastPage}
                onClick={() => onPageChange(page + 1)}
                style={s(
                  `padding:6px 12px;border:1px solid #D0D5DD;border-radius:6px;background:${page >= lastPage ? '#F2F4F7' : '#fff'};color:${page >= lastPage ? '#98A2B3' : '#344054'};cursor:${page >= lastPage ? 'not-allowed' : 'pointer'};font-size:12.5px;font-weight:500`
                )}
              >
                {t('managerNetwork.drilldown.next', 'Sau')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

